import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { AutoCompleteModule } from 'primeng/autocomplete';
import { CalendarModule } from 'primeng/calendar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DataViewModule } from 'primeng/dataview';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { PanelModule } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { TreeTableModule } from 'primeng/treetable';
import { InputNumberModule } from 'primeng/inputnumber';

import { HisSharedModule } from 'hisng';

import { SubjectAreaPlamComponent } from './component/classifications/subject-area/subject-area-plam.component';
import { ResearchAreaPlamComponent } from './component/classifications/research-area/research-area-plam.component';
import { ResearchAreaKdsfPlamComponent } from './component/classifications/research-area-kdsf/research-area-kdsf-plam.component';
import { OrganizationsPanelComponent } from './component/organizations/organizations-panel.component';
import { ClassificationsTabComponent } from './component/classifications/classifications-tab.component';
import {
    ResearchContractIncomesComponent
} from './component/finance/research-contract-income/edit/research-contract-incomes.component';
import { InternalOrganizationEditComponent } from './component/organizations/internal/edit/internal-organization-edit.component';
import { HistorizedOrganizationEditComponent } from './component/organizations/historized/edit/historized-organization-edit.component';
import { InternalOrganizationViewComponent } from './component/organizations/internal/view/internal-organization-view.component';
import { ExternalOrganizationViewComponent } from './component/organizations/external/view/external-organization-view.component';
import { ExternalOrganizationEditComponent } from './component/organizations/external/edit/external-organization-edit.component';
import { ConfirmationDialogComponent } from './component/confirmation-dialogs/confirmation-dialog.component';
import { InformationDialogComponent } from './component/confirmation-dialogs/information-dialog.component';
import { ResearchTabComponent } from './component/research/research-tab.component';
import { NominationPanelComponent } from './component/research/nomination/edit/nomination-panel.component';
import { ProjectPanelComponent } from './component/research/project/edit/project-panel.component';
import { PublicationPanelComponent } from './component/research/publication/edit/publication-panel.component';
import { DoctoratePanelComponent } from './component/research/doctorate/edit/doctorate-panel.component';
import { ExternalPersonCreateComponent } from './component/external-persons/create/external-person-create.component';
import { ExternalPersonCreateDialogComponent } from './component/popup-dialogs/external-person-create-dialog.component';
import { EditRightOfUseComponent } from './component/finance/right-of-use/edit/edit-right-of-use.component';
import { DocumentsEditComponent } from 'app/fs/res/common/component/documents/edit/documents-edit.component';
import { ExternalOrganizationCreateComponent } from './component/organizations/external/create/external-organization-create.component';
import { ExternalOrganisationCreateDialogComponent } from './component/popup-dialogs/external-organization-create-dialog.component';
import { CollapsibleTextViewComponent } from 'app/fs/res/common/component/display/collapsible-text-view.component';

import { PublicationDocumentationLinkComponent } from './component/display/documentation-link/publication-documentation-link.component';
import { PublisherDocumentationLinkComponent } from './component/display/documentation-link/publisher-documentation-link.component';
import { JournalDocumentationLinkComponent } from './component/display/documentation-link/journal-documentation-link.component';
import { ConferenceDocumentationLinkComponent } from './component/display/documentation-link/conference-documentation-link.component';
import { ProjectDocumentationLinkComponent } from './component/display/documentation-link/project-documentation-link.component';
import { PatentDocumentationLinkComponent } from './component/display/documentation-link/patent-documentation-link.component';
import { RelationDocumentationLinkComponent } from './component/display/documentation-link/relation-documentation-link.component';
import {
    InfrastructureDocumentationLinkComponent
} from './component/display/documentation-link/infrastructure-documentation-link.component';
import { EmploymentDocumentationLinkComponent } from './component/display/documentation-link/employment-documentation-link.component';
import { HabilitationDocumentationLinkComponent } from './component/display/documentation-link/habilitation-documentation-link.component';
import { DoctoralProgramDocumentationLinkComponent } from './component/display/documentation-link/docProgram-documentation-link.component';
import { BudgetSourceDocumentationLinkComponent } from './component/display/documentation-link/budgetSource-documentation-link.component';
import { PrizeDocumentationLinkComponent } from './component/display/documentation-link/prize-documentation-link.component';
import {
    ExternalOrganizationDocumentationLinkComponent
} from './component/display/documentation-link/externalOrganization-documentation-link.component';
import { EditResearchContractIncomeDialogComponent }
    from './component/finance/research-contract-income/edit/edit-research-contract-income-dialog.component';

import { YesNoPlamComponent } from './component/yesno/yes-no-plam.component';
import { DynamicTabContainerComponent } from './component/dynamic-content/dynamic-tab-container.component';
import { InfoPanelComponent } from './component/info-panel/info-panel.component';
import { ResearchInfrastructurePanelComponent }
    from './component/research/research-infrastructure/edit/research-infrastructure-panel.component';

import { DisplayDuplicatesComponent } from './component/duplicates-check/display-duplicates.component';
import { ConferencePlamComponent } from './component/plams/conference-plam.component';
import { JournalPlamComponent } from './component/plams/journal-plam.component';

/**
 * SharedModule for common components.
 */
@NgModule({
    imports: [
        CommonModule,
        RouterModule,
        FormsModule,
        ReactiveFormsModule,

        // primeng
        AutoCompleteModule,
        CalendarModule,
        ConfirmDialogModule,
        DataViewModule,
        DialogModule,
        FileUploadModule,
        InputNumberModule,
        PanelModule,
        TableModule,
        TreeTableModule,

        // HIS
        HisSharedModule
    ],

    declarations: [
        ConfirmationDialogComponent,
        InformationDialogComponent,
        ResearchContractIncomesComponent,
        EditRightOfUseComponent,
        CollapsibleTextViewComponent,
        YesNoPlamComponent,
        // Classification Tab + Panels
        ClassificationsTabComponent,
        SubjectAreaPlamComponent, // edit
        ResearchAreaPlamComponent, // edit
        ResearchAreaKdsfPlamComponent, // edit
        // Organization panel + sub components
        OrganizationsPanelComponent,
        InternalOrganizationViewComponent,
        InternalOrganizationEditComponent,
        ExternalOrganizationViewComponent,
        ExternalOrganizationEditComponent,
        HistorizedOrganizationEditComponent,
        // Research Tab + Panels
        ResearchTabComponent,
        NominationPanelComponent,
        ProjectPanelComponent,
        PublicationPanelComponent,
        DoctoratePanelComponent,
        ResearchInfrastructurePanelComponent,
        // Conference panel
        ConferencePlamComponent,
        // Journal panel
        JournalPlamComponent,

        ExternalPersonCreateDialogComponent,
        ExternalOrganisationCreateDialogComponent,
        ExternalPersonCreateComponent,
        ExternalOrganizationCreateComponent,
        DocumentsEditComponent,
        DisplayDuplicatesComponent,
        DynamicTabContainerComponent,
        InfoPanelComponent,
        EditResearchContractIncomeDialogComponent,

        // Deep Links to Documentation
        PublicationDocumentationLinkComponent,
        PublisherDocumentationLinkComponent,
        JournalDocumentationLinkComponent,
        ConferenceDocumentationLinkComponent,
        ProjectDocumentationLinkComponent,
        PatentDocumentationLinkComponent,
        RelationDocumentationLinkComponent,
        InfrastructureDocumentationLinkComponent,
        EmploymentDocumentationLinkComponent,
        HabilitationDocumentationLinkComponent,
        DoctoralProgramDocumentationLinkComponent,
        BudgetSourceDocumentationLinkComponent,
        ExternalOrganizationDocumentationLinkComponent,
        PrizeDocumentationLinkComponent
    ],

    exports: [
        ConfirmationDialogComponent,
        InformationDialogComponent,
        ResearchContractIncomesComponent,
        EditRightOfUseComponent,
        CollapsibleTextViewComponent,
        YesNoPlamComponent,
        // Classification Tab + Panels
        ClassificationsTabComponent,
        SubjectAreaPlamComponent, // edit
        ResearchAreaPlamComponent, // edit
        // Organization panel + sub components
        OrganizationsPanelComponent,
        InternalOrganizationEditComponent,
        InternalOrganizationViewComponent,
        ExternalOrganizationEditComponent,
        ExternalOrganizationViewComponent,
        HistorizedOrganizationEditComponent,
        // Research Tab + Panels
        ResearchTabComponent,
        NominationPanelComponent,
        ProjectPanelComponent,
        PublicationPanelComponent,
        DoctoratePanelComponent,
        ResearchInfrastructurePanelComponent,
        // Conference panel
        ConferencePlamComponent,
        // Journal panel
        JournalPlamComponent,

        ExternalPersonCreateDialogComponent,
        ExternalOrganisationCreateDialogComponent,
        ExternalPersonCreateComponent,
        ExternalOrganizationCreateComponent,
        DocumentsEditComponent,
        DisplayDuplicatesComponent,
        DynamicTabContainerComponent,
        InfoPanelComponent,
        EditResearchContractIncomeDialogComponent,

        // Deep Links to Documentation
        PublicationDocumentationLinkComponent,
        PublisherDocumentationLinkComponent,
        JournalDocumentationLinkComponent,
        ConferenceDocumentationLinkComponent,
        ProjectDocumentationLinkComponent,
        PatentDocumentationLinkComponent,
        RelationDocumentationLinkComponent,
        InfrastructureDocumentationLinkComponent,
        EmploymentDocumentationLinkComponent,
        HabilitationDocumentationLinkComponent,
        DoctoralProgramDocumentationLinkComponent,
        BudgetSourceDocumentationLinkComponent,
        ExternalOrganizationDocumentationLinkComponent,
        PrizeDocumentationLinkComponent
    ]
    // NO PROVIDERS! Providers need to go into CommonCoreModule
})
export class CommonSharedModule {
}
